import { LoadingState } from '@/components/loading-state'
import { MagnifyingGlass, NavigationArrow } from '@phosphor-icons/react'
import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { discoveryApi } from '../api'
import { geolocationErrorKey } from '../geo'
import { captureAmbientLocation } from '../location'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import type { DiscoveryLocation } from '../types'
import { useDebouncedValue } from '../hooks/ui'

const MIN_QUERY_LENGTH = 2

interface CitySuggestion {
  city: string
  label: string
  latitude: number
  longitude: number
}

interface SearchResult {
  query: string
  phase: 'ready' | 'error'
  items: CitySuggestion[]
  errorKey: string | null
}

interface CitySearchProps {
  onSelect: (location: DiscoveryLocation) => void
  /**
   * Optional manual detection flow. When provided, the picker owns the ambient
   * location/profile side effects and the caller closes the sheet afterwards.
   * The filter sheet leaves it unset and keeps the explicit-center behavior.
   */
  onLocate?: () => Promise<void>
  focusOnMount?: boolean
}

/**
 * Shared city search: Photon suggestions through the backend only (never a
 * third-party geocoder from the client) plus the explicit GPS fallback.
 */
export function CitySearch({
  onSelect,
  onLocate,
  focusOnMount = false,
}: CitySearchProps) {
  const { t } = useTranslation()
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const debounced = useDebouncedValue(query.trim(), 400)
  const [result, setResult] = useState<SearchResult>({
    query: '',
    phase: 'ready',
    items: [],
    errorKey: null,
  })
  const [attempt, setAttempt] = useState(0)
  const [locating, setLocating] = useState(false)
  const [locationErrorKey, setLocationErrorKey] = useState<string | null>(null)

  useEffect(() => {
    if (!focusOnMount) return
    inputRef.current?.focus()
  }, [focusOnMount])

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) return
    const controller = new AbortController()
    discoveryApi
      .suggestCities(debounced, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setResult({
          query: debounced,
          phase: 'ready',
          items: response.items,
          errorKey: null,
        })
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setResult({
          query: debounced,
          phase: 'error',
          items: [],
          errorKey: friendlyErrorKey(cause),
        })
      })
    return () => controller.abort()
  }, [attempt, debounced])

  const shortQuery = debounced.length < MIN_QUERY_LENGTH
  const current = result.query === debounced
  const phase = shortQuery
    ? 'idle'
    : current
      ? result.phase
      : 'loading'
  const suggestions = current ? result.items : []

  async function requestMyLocation() {
    if (locating) return
    setLocating(true)
    setLocationErrorKey(null)
    try {
      if (onLocate) {
        await onLocate()
        return
      }
      const detected = await captureAmbientLocation()
      onSelect({
        city: detected.city,
        latitude: detected.latitude,
        longitude: detected.longitude,
      })
    } catch (error) {
      setLocationErrorKey(geolocationErrorKey(error))
    } finally {
      setLocating(false)
    }
  }

  function retry() {
    setResult({ query: '', phase: 'ready', items: [], errorKey: null })
    setAttempt((value) => value + 1)
  }

  return (
    <div>
      <label htmlFor={inputId} className="field-label">
        {t('discovery.city.searchLabel')}
      </label>
      <div className="relative">
        <MagnifyingGlass
          size={19}
          weight="bold"
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted"
        />
        <input
          id={inputId}
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('discovery.city.searchPlaceholder')}
          autoComplete="off"
          className="field-input pl-10"
        />
      </div>
      <p className="mt-1 text-xs text-muted">{t('discovery.city.minChars')}</p>

      <button
        type="button"
        className="mt-3 flex min-h-12 w-full items-center gap-3 rounded-md border border-line bg-white px-3 text-left hover:bg-mint"
        onClick={() => void requestMyLocation()}
        disabled={locating}
        aria-busy={locating}
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-mint text-teal">
          <NavigationArrow size={18} weight="bold" aria-hidden="true" />
        </span>
        <span className="font-semibold">
          {locating
            ? t('discovery.city.locating')
            : t('discovery.city.useLocation')}
        </span>
      </button>

      {locationErrorKey ? (
        <p
          role="alert"
          className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger"
        >
          {t(locationErrorKey)}
        </p>
      ) : null}

      {phase === 'loading' ? (
        <LoadingState compact labelKey="discovery.city.loading" />
      ) : null}

      {phase === 'error' ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted">
          <span>
            {result.errorKey ? t(result.errorKey) : t('discovery.city.error')}
          </span>
          <button type="button" className="btn-ghost min-h-11" onClick={retry}>
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {phase === 'ready' && suggestions.length === 0 ? (
        <p className="mt-4 rounded-md border border-line bg-white px-3 py-4 text-sm text-muted">
          {t('discovery.city.empty')}
        </p>
      ) : null}

      {suggestions.length > 0 ? (
        <ul className="mt-3 divide-y divide-line overflow-hidden rounded-md border border-line bg-white">
          {suggestions.map((suggestion) => (
            <li
              key={`${suggestion.city}-${suggestion.latitude}-${suggestion.longitude}`}
            >
              <button
                type="button"
                className="flex min-h-12 w-full flex-col items-start justify-center px-3 py-2 text-left hover:bg-mint"
                onClick={() =>
                  onSelect({
                    city: suggestion.city,
                    latitude: suggestion.latitude,
                    longitude: suggestion.longitude,
                  })
                }
              >
                <span className="font-semibold">{suggestion.city}</span>
                {suggestion.label && suggestion.label !== suggestion.city ? (
                  <span className="text-xs text-muted">{suggestion.label}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
